import { produce } from 'immer'
import { requestBudgetRouteProps } from 'src/navigation/routeProps'
import {
  POST_CREDIT_CARD_FAILURE,
  POST_CREDIT_CARD_INIT,
  POST_CREDIT_CARD_SUCCESS,
  PostCreditCardActions,
} from 'src/redux/actions/data/creditCards.actions'
import CreditCardsFullPaginatedDataReducer, {
  Actions as CreditCardsFullPaginatedDataActions,
  CreditCardsFullPaginatedDataState,
  initialState as initialCreditCardsFullPaginatedDataState,
} from 'src/redux/creditCardsFullPaginatedData.reducer'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import RecentUsersFullPaginatedDataReducer, {
  Actions as RecentUsersFullPaginatedDataActions,
  initialState as initialRecentUsersFullPaginatedDataState,
  RecentUsersFullPaginatedDataState,
} from 'src/redux/recentUsersFullPaginatedData.reducer'
import UsersFullPaginatedDataReducer, {
  Actions as UsersFullPaginatedDataActions,
  initialState as initialUsersFullPaginatedDataState,
  UsersFullPaginatedDataState,
} from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface RequestBudgetBaseState {
  postingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
}

export const initialBaseState: RequestBudgetBaseState = {
  postingCreditCard: false,
}

export type BaseActions = PostCreditCardActions

const BaseRequestBudgetReducer = (
  state = initialBaseState,
  action: BaseActions,
): RequestBudgetBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_CREDIT_CARD_INIT: {
        draft.postingCreditCard = true
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case POST_CREDIT_CARD_FAILURE: {
        draft.postingCreditCard = false
        const errors = parseBackendErrors(action.payload)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_CREDIT_CARD_SUCCESS: {
        draft.postingCreditCard = false
        return draft
      }
    }
  })
}

export type RequestBudgetState = RequestBudgetBaseState & {
  creditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  usersFullPaginatedData: UsersFullPaginatedDataState
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataState
}

export const initialState: RequestBudgetState = {
  ...initialBaseState,
  creditCardsFullPaginatedData: initialCreditCardsFullPaginatedDataState,
  usersFullPaginatedData: initialUsersFullPaginatedDataState,
  recentUsersFullPaginatedData: initialRecentUsersFullPaginatedDataState,
}

export type Actions =
  | BaseActions
  | CreditCardsFullPaginatedDataActions
  | UsersFullPaginatedDataActions
  | RecentUsersFullPaginatedDataActions

const CoreRequestBudgetReducer = (
  state = initialState,
  action: Actions,
): RequestBudgetState => ({
  ...BaseRequestBudgetReducer(state, action as BaseActions),
  creditCardsFullPaginatedData: CreditCardsFullPaginatedDataReducer(
    state.creditCardsFullPaginatedData,
    action as CreditCardsFullPaginatedDataActions,
  ),
  usersFullPaginatedData: UsersFullPaginatedDataReducer(
    state.usersFullPaginatedData,
    action as UsersFullPaginatedDataActions,
  ),
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataReducer(
    state.recentUsersFullPaginatedData,
    action as RecentUsersFullPaginatedDataActions,
  ),
})

const RequestBudgetReducer = createFlyoutReducer(
  requestBudgetRouteProps,
  initialState,
  CoreRequestBudgetReducer,
)

export default RequestBudgetReducer
